/**
 * 官网与工作台不同主域，cookie 无法共享，推广归因只能靠 URL 参数 + 本地存储。
 * 绑定是一对一永久关系，所以本地已有未过期的邀请码时先到先得，不覆盖。
 */

const referralStorageKey = 'wxproto-desk-referral'
const referralTtlMs = 90 * 24 * 60 * 60 * 1000
const referralCodePattern = /^[A-Z0-9]{4,16}$/

type StoredReferral = { code: string; expiresAt: number }

function normalizeReferralCode(raw: string): string {
  const code = raw.trim().toUpperCase()
  return referralCodePattern.test(code) ? code : ''
}

/** Returns the stored referral code, dropping it once expired or malformed. */
export function readStoredReferral(): string {
  try {
    const raw = window.localStorage.getItem(referralStorageKey)
    if (!raw) return ''
    const parsed = JSON.parse(raw) as Partial<StoredReferral>
    const code = normalizeReferralCode(String(parsed?.code || ''))
    const expiresAt = typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : 0
    if (!code || expiresAt <= Date.now()) {
      window.localStorage.removeItem(referralStorageKey)
      return ''
    }
    return code
  } catch {
    return ''
  }
}

/** 地址栏上的邀请码，供 localStorage 写入失败时兜底预填注册表单。 */
export function readReferralFromUrl(): string {
  if (typeof window === 'undefined') return ''
  return normalizeReferralCode(new URLSearchParams(window.location.search).get('ref') || '')
}

export function clearStoredReferral(): void {
  try {
    window.localStorage.removeItem(referralStorageKey)
  } catch {
    // Storage may be unavailable (private mode); never break the caller.
  }
}

function persistReferral(code: string): boolean {
  try {
    const payload: StoredReferral = { code, expiresAt: Date.now() + referralTtlMs }
    window.localStorage.setItem(referralStorageKey, JSON.stringify(payload))
    return true
  } catch {
    return false
  }
}

/** Capture `?ref=` on startup, then strip it so the user can't reshare someone else's code. */
export function captureReferralFromUrl(): void {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  if (!params.has('ref')) return

  // 落盘失败（隐私模式）时保留地址栏的 ref，否则归因就彻底丢了。
  const code = normalizeReferralCode(params.get('ref') || '')
  if (code && !readStoredReferral() && !persistReferral(code)) return

  params.delete('ref')
  const search = params.toString()
  window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`)
}
